import { useQuery } from '@tanstack/react-query'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../config/firebase.js'
import { useCloudContext } from '../contexts/CloudProvider.jsx'

// Firebase Function reference - singleton for optimal performance
const getHistoricalDataFn = httpsCallable(functions, 'getHistoricalData')

// Constants for better tree-shaking and performance
const EMPTY_ARRAY = Object.freeze([])
const CACHE_TIME = 5 * 60 * 1000 // 5 minutes

const CHARGE_STAGE_NAME_MAPPING = {
  cv: 'Bulk / Absorb',
  postCv: 'Second Absorb',
  float: 'Float',
}

/**
 * Data adapter to convert Firebase Function response to express-compatible format
 */
const adaptChargeStageResponse = (firebaseResponse) => {
  if (!firebaseResponse?.data?.success || !firebaseResponse.data.data) {
    return []
  }
  
  return firebaseResponse.data.data.map(row => ({
    _time: row._time,
    _measurement: row._measurement,
    _field: row._field,
    _value: row._value,
    device: row.device ?? row.serialNumber,
    serialNumber: row.serialNumber ?? row.device,
    source: row.dataSource ?? row.source,
  }))
}

/**
 * Query function for charge stage data using Firebase Functions
 * Optimized for minimal re-renders and maximum efficiency
 */
const fetchChargeStageData = async ({ queryKey, signal }) => {
  const [_key, chargeStageDevice, chargeStageMeasurement, currentXAxisRange, selectedDeviceId] = queryKey

  if (!chargeStageDevice || !chargeStageMeasurement || !currentXAxisRange?.min || !currentXAxisRange?.max) {
    return EMPTY_ARRAY
  }

  // Check if query was aborted
  if (signal?.aborted) {
    throw new Error('Query aborted')
  }

  try {
    const startTime = new Date(currentXAxisRange.min).toISOString()
    const endTime = new Date(currentXAxisRange.max).toISOString()

    // In connect environment, use selectedDeviceId as serialNumber and chargeStageDevice as device component
    const response = await getHistoricalDataFn({
      serialNumber: selectedDeviceId || chargeStageDevice, // Fallback to chargeStageDevice for express compatibility
      device: chargeStageDevice, // Device component like 'bat1'
      measurement: chargeStageMeasurement,
      start: startTime,
      end: endTime,
    })

    return adaptChargeStageResponse(response)
  } catch (error) {
    // Don't log if it was just an abort
    if (!signal?.aborted) {
      console.error('Charge stage data fetch failed:', error)
    }
    return EMPTY_ARRAY
  }
}

/**
 * Custom hook for charge stage data optimized for React 19+ and Firebase v11+
 * Maintains express compatibility with minimal re-renders and optimal caching
 * 
 * @param {string} chargeStageDevice - Device component (e.g., 'bat1') to fetch charge stage data for
 * @param {string} chargeStageMeasurement - Measurement to fetch (usually 'chargeStage')
 * @param {Object} currentXAxisRange - Current chart x-axis range with min/max timestamps
 * @returns {Object} Charge stage data, loading state, error, and name mapping
 */
export const useChargeStageData = (chargeStageDevice, chargeStageMeasurement, currentXAxisRange) => {
  // Get the selected device from context for connect environment
  const { selectedDeviceId } = useCloudContext()
  
  // Validate inputs for query enablement
  const chartIsReadyForFetch = currentXAxisRange && currentXAxisRange.min !== null && currentXAxisRange.max !== null
  const chargeStagePropsAvailable = chargeStageDevice && chargeStageMeasurement

  const { 
    data: chargeStageTooltipData, 
    isLoading, 
    error, 
  } = useQuery({
    queryKey: ['chargeStageData', chargeStageDevice, chargeStageMeasurement, currentXAxisRange, selectedDeviceId],
    queryFn: fetchChargeStageData,
    enabled: !!(chartIsReadyForFetch && chargeStagePropsAvailable), // Ensure boolean
    staleTime: CACHE_TIME,
    gcTime: CACHE_TIME * 2, // v5 syntax for cacheTime
    refetchOnWindowFocus: false,
    retry: 2,
  })

  return { 
    chargeStageTooltipData: chargeStageTooltipData || EMPTY_ARRAY, // Ensure it's always an array
    CHARGE_STAGE_NAME_MAPPING, 
    isLoadingChargeStage: isLoading, 
    chargeStageError: error?.message ?? null, // Pass error message
  }
}
